// channelMembersSlice.js
import { createSlice } from '@reduxjs/toolkit';
import logger from '../Helpers/logger';

const channelMembersSlice = createSlice({
    name: 'channelMembersData',
    initialState: {
        membersByChannel: {}, // { [channelId]: { members: [], total, loading, error } }
    },
    reducers: {
        setMembers(state, action) {
            const { channelId, members, total, totalMembers, page } = action.payload;

            if (!state.membersByChannel[channelId]) {
                state.membersByChannel[channelId] = {
                    members: [],
                    total: 0,
                    totalActive: 0,
                    loading: false,
                    error: null,
                };
            }

            const channel = state.membersByChannel[channelId];

            if (page === 1) {
                channel.members = members || [];
            } else {
                const existing = channel.members;
                const merged = [...existing, ...members];
                const seen = new Set();
                channel.members = merged.filter(m => {
                    if (seen.has(m.userId)) {
                        logger.warn('🚨 Duplicate userId found:', m.userId, m);
                        return false;
                    }
                    seen.add(m.userId);
                    return true;
                });
            }

            channel.total = total ?? channel.members.length;

            // ✅ trust server count if available
            channel.totalActive = totalMembers;
            channel.loading = false;
            channel.error = null;
        },

        applyMemberEvents(state, action) {
            const { channelId, memberEvents } = action.payload;

            if (!state.membersByChannel[channelId]) {
                state.membersByChannel[channelId] = {
                    members: [],
                    total: 0,
                    totalActive: 0,
                    loading: false,
                    error: null,
                };
            }

            const channel = state.membersByChannel[channelId];
            const map = new Map(channel.members.map(m => [m.userId, m]));
            let activeCount = channel.totalActive;
            memberEvents.forEach(({ type, member }) => {
                const userId = member.userId;
                const existing = map.get(userId);

                if (type === 'ADD' || type === 'JOIN') {
                    // if new or previously inactive, increment
                    if (!existing || existing.status === 0) {
                        activeCount++;
                    }
                    map.set(userId, { ...member });
                } else if (type === 'LEAVE' && existing) {
                    // if leaving but was active, decrement
                    if (existing.status !== 0) {
                        activeCount--;
                    }
                    map.set(userId, { ...existing, status: 0 });
                } else if (type === 'ROLE_CHANGED' && existing) {
                    map.set(userId, { ...existing, roleId: member.roleId });
                }
            });

            channel.members = Array.from(map.values());
            channel.total = channel.members.length;
            channel.totalActive = Math.max(activeCount, 0); // prevent negatives
        },

        setLoading(state, action) {
            const { channelId, loading } = action.payload;
            if (!state.membersByChannel[channelId]) {
                state.membersByChannel[channelId] = { members: [], total: 0, loading, error: null };
            }
            state.membersByChannel[channelId].loading = loading;
        },
        setError(state, action) {
            const { channelId, error } = action.payload;
            if (!state.membersByChannel[channelId]) {
                state.membersByChannel[channelId] = { members: [], total: 0, loading: false, error };
            }
            state.membersByChannel[channelId].error = error;
        },
        resetChannel(state, action) {
            const { channelId } = action.payload;
            state.membersByChannel[channelId] = { members: [], total: 0, loading: false, error: null };
        },
    },
});

export const { setMembers, setLoading, setError, applyMemberEvents, resetChannel } = channelMembersSlice.actions;
export default channelMembersSlice.reducer;
